import { Component, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Color, ScaleType } from '@swimlane/ngx-charts';
import { AbstractComponent, Campaign, CampaignMessage, CampaignMessageLatency, CampaignMessageLatencyBand, CampaignMessageService, CampaignMessageStatusEnum, CampaignProgressSnapshot, CampaignService, CampaignStatusEnum, Channel, ChannelTypeEnum, CloudAPIData, MetadataCampaign, Pager, RCSData, Tag, ZAPIData } from 'lib-trend-core';
import { catchError, EMPTY, finalize, takeUntil } from 'rxjs';
import { CampaignMessagePayloadComponent } from '../campaign-message-payload/campaign-message-payload.component';
import { CampaignRefreshPreferenceService } from '../shared/campaign-refresh-preference.service';

/** One line of the latency chart — a state (sent / delivered / read), one point per band. */
interface LatencySeries {
  name: string;
  series: Array<{ name: string; value: number }>;
}

interface ProgressSegment {
  key: string;
  labelKey: string;
  count: number;
  percent: number;
  tone: 'read' | 'ok' | 'warn' | 'err' | 'queue';
}

const MINUTE_MS = 60_000;

@Component({
    selector: 'campaign-message',
    templateUrl: './campaign-message.v2.component.html',
    // templateUrl: './campaign-message.component.html',
    styleUrls: ['./campaign-message.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CampaignMessageComponent extends AbstractComponent implements OnInit {

  pager: Pager<CampaignMessage> = new Pager<CampaignMessage>({ perPage: 100 });
  // 'CLICKED' is not a status: the API turns it into a filter on the `clicked` flag,
  // because a click is orthogonal to the dispatch outcome (the row stays SUCCESS).
  statusList: string[] = ['ERROR', 'SUCCESS', 'PENDING', 'CLICKED', 'TOTAL'];
  statusControl = new FormControl('TOTAL');

  statusTranslations: { [key: string]: string } = {
    'ERROR': 'campaign.message.filterError',
    'SUCCESS': 'campaign.message.filterSuccess',
    'TOTAL': 'campaign.message.filterTotal',
    'PENDING': 'campaign.message.filterPending',
    'CLICKED': 'campaign.message.filterClicked',
  };

  campaignStatus: { [key in CampaignStatusEnum]: string };
  campaignStatusEnum = CampaignStatusEnum;

  audienceTags: Tag[] = [];
  channelType: string;
  channelTypeEnum = ChannelTypeEnum;

  campaign: Campaign;
  selectedTemplate: any;
  selectedTemplateDataInit: any;
  selectedChannel: Channel;
  progress: CampaignProgressSnapshot | null = null;
  totalSentCount: number = 0;
  deliveredCount: number = 0;
  readCount: number = 0;
  clickedCount: number = 0;
  totalFailedCount: number = 0;
  totalPendingCount: number = 0;
  resolvedCount: number = 0;
  unconfirmedCount: number = 0;
  deliveredUnreadCount: number = 0;
  withheldCount: number = 0;
  batchTotal: number = 0;
  deliveryRate: number | null = null;
  acceptanceRate: number | null = null;

  failedRate: number = 0;
  pendingRate: number = 0;

  /** Latest `GET /campaign-message/latency/:id` reading; `null` until the first one arrives. */
  latency: CampaignMessageLatency | null = null;

  readRate: number | null = null;
  clickRate: number | null = null;

  latencyChart: LatencySeries[] = [];
  /** Series names in color order — the chart's own legend is drawn from this. */
  chartSeriesNames: string[] = [];
  /**
   * Sent in the brand color; Delivered and Read in the SAME colors the progress
   * bar uses for the same segments (`--st-ok`, `--st-read`): color follows the
   * entity, so the operator who watched the dispatch on the bar finds the same
   * green and orange on the chart that replaces it. Resolved from the theme in
   * `ngOnInit`, never hardcoded — they follow dark mode and any token change.
   */
  readonly chartColors: Color = {
    name: 'campaignLatency',
    selectable: false,
    group: ScaleType.Ordinal,
    domain: ['var(--st-brand)', 'var(--st-ok)', 'var(--st-read)'],
  };

  private static readonly MIN_ELAPSED_FOR_RATE_MS = 20_000;

  nonTemplateData: MetadataCampaign = {};

  private pollingConfigured = false;

  constructor(
    injector: Injector,
    private campaignMessageService: CampaignMessageService,
    public campaignService: CampaignService,
    public dialog: MatDialog,
    private refreshPreference: CampaignRefreshPreferenceService,
  ) {
    super(injector);
  }

  async ngOnInit(): Promise<void> {
    const idCampaign = this.getParam('id');

    this.chartColors.domain = [
      this.themeColor('--st-brand'),
      this.themeColor('--st-ok'),
      this.themeColor('--st-read'),
    ];

    // Subscribed once here, not in init(): the manual sync button re-enters
    // init(), and every extra subscription would fire one more duplicate
    // request on each filter change from then on.
    this.statusControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.applyFilter());

    if (idCampaign) {
      this.init(idCampaign);
    }
  }

  init(idCampaign: string) {
    this.loading = true;
    this.getCampaign(idCampaign);
    this.getCampaignMessagesByCampaign(idCampaign);
  }

  /**
   * The computed value of a `--st-*` theme token.
   *
   * ngx-charts paints bars through a direct `fill`, so it needs the resolved
   * color, not the variable name. `getComputedStyle` already substitutes the
   * `var()` chain (`--st-ok` → `--ct-success` → hex). When the token is not
   * declared on the document, the `var()` reference itself is returned: SVG
   * accepts it as a `fill` value and the chart stays readable instead of black.
   */
  private themeColor(token: string): string {
    const resolved = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
    return resolved || `var(${token})`;
  }

  private getCampaign(id: string): void {
    this.campaignService.getById(id).subscribe({
      next: (campaign) => {
        this.campaign = campaign;

        this.audienceTags = this.campaign.audienceTags;

        this.selectedTemplate = campaign.metadata.template;
        this.nonTemplateData = campaign.metadata;
        this.channelType = campaign.channel.type as ChannelTypeEnum;

        this.selectedChannel = campaign.channel;

        this.selectedTemplateDataInit = {
          params: campaign.metadata.templateParams,
          imageFile: campaign.metadata.templateImage,
        };

        this.configPolling();
      },
    });
  }

  getCampaignMessagesByCampaign(idCampaign: string): void {

    this.searchParams = {
      campaign: idCampaign,
      company: this.getIDCurrentCompany(),
    };

    // Only when it filters something. The params are appended to the query string verbatim,
    // so a `status: null` travels as the literal "status=null" — which the API can only read
    // as a status nothing matches, emptying the very list the operator asked to see whole.
    const selectedStatus = this.statusControl.value;
    if (!!selectedStatus && selectedStatus !== 'TOTAL') {
      this.searchParams['status'] = selectedStatus;
    }

    this.campaignMessageService.getAll(this.pager.page, this.pager.perPage, this.searchString, this.searchParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (pager: Pager<CampaignMessage>) => {
          this.pager = pager;
          this.loadLiveState(idCampaign);
        },
        complete: () => this.loading = false,
      });
  }

  loadPage(page: number) {
    this.pager.page = page;
    this.getCampaignMessagesByCampaign(this.campaign._id);
  }

  /**
   * A filter change always restarts at page 1 and rebuilds the params in one place.
   *
   * Building them here too is what let them drift: this copy sent `status: null` for
   * "Todos" — appended to the URL as "status=null" — and kept whatever page the operator
   * was on, so a narrower result set answered with an empty page.
   */
  applyFilter() {
    this.pager.page = 1;
    this.getCampaignMessagesByCampaign(this.campaign?._id);
  }

  formatTags(): { title: string; bgColor: string; fontColor: string }[] {
    return this.audienceTags?.map(tag => ({
      title: tag.title,
      bgColor: tag.bgColor || '#ccc',
      fontColor: tag.fontColor || '#FFF'
    })) || [];
  }

  /**
   * How a row reads on screen — the badge the table prints, and the same rule the API's
   * filter applies (`CAMPAIGN_MESSAGE_OUTCOME_FILTERS` in `campaign-message.controller.ts`).
   *
   * The flags, never `status`: `status` freezes the provider's answer at SEND time, while
   * every receipt that arrives afterwards moves only the flags — an RCS delivery failure
   * leaves `status: SUCCESS` and sets `failed`.
   */
  resolveMessageStatus(campaignMessage: CampaignMessage): string {
    if (campaignMessage?.failed) {
      return CampaignMessageStatusEnum.ERROR;
    }
    if (campaignMessage?.read || campaignMessage?.delivered || campaignMessage?.sent) {
      return CampaignMessageStatusEnum.SUCCESS;
    }
    return campaignMessage?.status ?? CampaignMessageStatusEnum.PENDING;
  }

  /**
   * Who the row was sent to. A recipient from an uploaded list is not a contact:
   * the row carries only the snapshot taken from the file, and it stays that way
   * until the person replies. Reading `contact` alone left the whole column blank
   * for every file audience.
   */
  recipientName(campaignMessage: CampaignMessage): string {
    return campaignMessage?.contact?.name || campaignMessage?.recipient?.name || '';
  }

  recipientPhone(campaignMessage: CampaignMessage): string {
    return campaignMessage?.contact?.phone || campaignMessage?.recipient?.phone || campaignMessage?.recipientPhone || '';
  }

  view(campaignMessage: CampaignMessage): void {
    // A clicked row is worth opening even without a payload — the dialog lists the taps.
    if (!campaignMessage?.payload && !campaignMessage?.clicked) {
      this.alertService.info(this.t('campaign.message.payloadNotFound'));
      return;
    }

    this.dialog.open(CampaignMessagePayloadComponent, {
      width: '700px',
      data: campaignMessage
    });
  }


  loadLiveState(idCampaign: string): void {
    this.campaignMessageService.getCampaignProgress(idCampaign)
      .pipe(catchError(() => EMPTY), takeUntil(this.destroy$))
      .subscribe((progress) => this.applyProgress(progress));
  }

  private lastLatencySignature: string | null = null;

  private refreshLatency(idCampaign: string): void {
    // The chart only shows once the batch closes; while the progress bar owns
    // that spot, fetching it would request data the screen cannot display.
    if (this.isLive) {
      return;
    }
    // The latency aggregation scans every message of the dispatch; on a
    // finished campaign a poll tick would fetch identical data forever. Only
    // refetch when the receipt counters actually moved (called after
    // applyProgress, so the counters below are the fresh ones).
    const signature = `${this.totalSentCount}|${this.deliveredCount}|${this.readCount}|${this.totalFailedCount}`;
    if (signature === this.lastLatencySignature) {
      return;
    }
    this.lastLatencySignature = signature;
    this.campaignMessageService.getCampaignLatency(idCampaign)
      .pipe(catchError(() => EMPTY), takeUntil(this.destroy$))
      .subscribe((latency) => this.applyLatency(latency));
  }

  /* ────────────────────────────────────────────────────────────────────
     Periodic refresh (DEV-2034 — polling instead of push)
     ──────────────────────────────────────────────────────────────────── */

  /**
   * Reloads the progress AND the recipient table at the cadence chosen in the
   * selector — the same preference the dispatch list uses. Runs in any status
   * while the screen is open: delivery/read receipts keep arriving after DONE,
   * and each one moves both a KPI counter and a row's "delivered"/"read"
   * column, so the table has to follow the same tick as the cards. Only with
   * the tab visible: leaving it pauses the polling, and coming back triggers
   * an immediate refresh instead of waiting for the next tick.
   */
  private configPolling(): void {
    if (this.pollingConfigured || !this.campaign?._id) {
      return;
    }
    this.pollingConfigured = true;
    const idCampaign = this.campaign._id;

    this.refreshPreference.ticks$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refreshQuietly(idCampaign));
  }

  /**
   * One polling tick: progress and table, fired together and independently.
   * Neither waits for the other — a failed progress read must not leave the
   * table stale, and a slow page read must not hold the KPIs back.
   */
  private refreshQuietly(idCampaign: string): void {
    this.loadLiveState(idCampaign);
    this.refreshMessagesQuietly(idCampaign);
  }

  private applyProgress(snapshot: CampaignProgressSnapshot): void {
    if (!snapshot) {
      return;
    }
    if (this.progress && snapshot.at && this.progress.at && snapshot.at < this.progress.at) {
      return;
    }

    this.progress = snapshot;

    this.totalSentCount = snapshot.sent ?? 0;
    this.deliveredCount = snapshot.delivered ?? 0;
    this.readCount = snapshot.read ?? 0;
    this.clickedCount = snapshot.clicked ?? 0;
    this.totalPendingCount = snapshot.pending ?? 0;
    this.totalFailedCount = snapshot.failed ?? 0;
    this.resolvedCount = snapshot.resolved ?? 0;
    this.unconfirmedCount = snapshot.unconfirmed ?? 0;
    this.deliveredUnreadCount = snapshot.deliveredUnread ?? 0;
    this.withheldCount = snapshot.withheld ?? 0;
    this.batchTotal = snapshot.dispatchable ?? 0;

    if (this.campaign && snapshot.status) {
      this.campaign.status = snapshot.status as CampaignStatusEnum;
      this.campaign.startedAt = snapshot.startedAt ?? this.campaign.startedAt;
      this.campaign.endedAt = snapshot.endedAt ?? this.campaign.endedAt;
      // `??`, not `||`: an empty string from the backend means the reason was
      // cleared (a re-run succeeded) and must clear it here too.
      this.campaign.executionMessage = snapshot.executionMessage ?? this.campaign.executionMessage;
      if (snapshot.cost?.settled && snapshot.cost.estimated !== null) {
        this.campaign.cost = snapshot.cost.estimated;
      }
    }

    this.deliveryRate = this.totalSentCount > 0
      ? Math.min(100, (this.deliveredCount / this.totalSentCount) * 100)
      : null;

    this.acceptanceRate = this.resolvedCount > 0
      ? (this.totalSentCount / this.resolvedCount) * 100
      : null;

    this.readRate = this.readCount > 0 && this.deliveredCount > 0
      ? (this.readCount / this.deliveredCount) * 100
      : null;

    // Measured against delivered, not sent: only a message that reached the handset
    // could be tapped. Null on a channel with no receipts, so the card shows a dash
    // rather than a rate computed from a zero denominator.
    this.clickRate = this.clickedCount > 0 && this.deliveredCount > 0
      ? Math.min(100, (this.clickedCount / this.deliveredCount) * 100)
      : null;

    this.failedRate = this.rateOfBatch(this.totalFailedCount);
    this.pendingRate = this.rateOfBatch(this.totalPendingCount);

    this.onProgressApplied();
  }

  // The table is NOT refreshed from here on purpose. It rides the polling tick
  // itself (refreshQuietly), in every status: gating it on "is running" left
  // the rows frozen on a finished dispatch while the KPI cards kept moving
  // with the late delivery/read receipts.
  private onProgressApplied(): void {
    const idCampaign = this.campaign?._id;
    if (!idCampaign) {
      return;
    }
    this.refreshLatency(idCampaign);
  }

  private messagesRefreshInFlight = false;

  /**
   * Silent reload of the recipient page on screen — no spinner, no error
   * toast. Quiet on purpose: re-entering getCampaignMessagesByCampaign here
   * would chain back into loadLiveState and double the tick's requests.
   */
  private refreshMessagesQuietly(idCampaign: string): void {
    // Skip the tick while the previous one is still in flight: stacking
    // duplicate page reads against a slow backend only makes it slower.
    if (this.messagesRefreshInFlight) {
      return;
    }
    this.messagesRefreshInFlight = true;

    const requestedPage = this.pager.page;
    const requestedStatus = this.statusControl.value;

    const params = {
      campaign: idCampaign,
      company: this.getIDCurrentCompany(),
    };

    if (!!requestedStatus && requestedStatus !== 'TOTAL') {
      params['status'] = requestedStatus;
    }

    this.campaignMessageService.getAll(this.pager.page, this.pager.perPage, this.searchString, params)
      .pipe(
        catchError(() => EMPTY),
        finalize(() => (this.messagesRefreshInFlight = false)),
        takeUntil(this.destroy$),
      )
      .subscribe((pager: Pager<CampaignMessage>) => {
        // A late response must never clobber a page or status filter the
        // operator changed while the request was in flight.
        if (requestedPage !== this.pager.page || requestedStatus !== this.statusControl.value) {
          return;
        }
        this.pager = pager;
      });
  }

  /* ────────────────────────────────────────────────────────────────────
     Leituras derivadas do andamento
     ──────────────────────────────────────────────────────────────────── */

  get isRunning(): boolean {
    return this.campaign?.status === CampaignStatusEnum.SENDING;
  }

  /**
   * Why the dispatch stopped — `Campaign.executionMessage`, only while the
   * status is ERROR. The pipeline writes the reason in the operator's words
   * ("insufficient balance: needed 1, available 0"), and the status alone gives
   * the operator nothing to act on. Outside ERROR the field may hold a stale
   * message from an earlier run, so it is not shown.
   */
  get failureMessage(): string | null {
    if (this.campaign?.status !== CampaignStatusEnum.ERROR) {
      return null;
    }
    const message = this.campaign?.executionMessage?.trim();
    return message ? message : null;
  }

  get isLive(): boolean {
    return this.isRunning || this.campaign?.status === CampaignStatusEnum.WAITING;
  }

  get showProgress(): boolean {
    return this.isLive && !!this.progress && this.batchTotal > 0;
  }

  get receiptsAvailable(): boolean {
    return this.deliveredCount > 0 || this.readCount > 0;
  }

  /**
   * Whether to show the click column and KPI at all.
   *
   * Only RCS reports suggestion taps, so on every other channel the column would be a
   * wall of dashes and the KPI a permanent zero. A tap already recorded is shown even
   * if the channel reading somehow disagrees — never hide data that exists.
   */
  get supportsClicks(): boolean {
    return this.channelType === ChannelTypeEnum.RCS_API || this.clickedCount > 0;
  }

  /** Já resolvidas — saíram da fila, seja aceitas ou recusadas. */
  get concludedCount(): number {
    return Math.max(0, this.batchTotal - this.totalPendingCount);
  }

  get concludedPercent(): number {
    return this.batchTotal > 0 ? (this.concludedCount / this.batchTotal) * 100 : 0;
  }

  /** Milissegundos entre o início do disparo e agora — ou o fim, se acabou. */
  private get elapsedMs(): number | null {
    const startedAt = this.campaign?.startedAt ? new Date(this.campaign.startedAt).getTime() : null;
    if (!startedAt) {
      return null;
    }
    const endedAt = this.campaign?.endedAt ? new Date(this.campaign.endedAt).getTime() : Date.now();
    return Math.max(0, endedAt - startedAt);
  }

  /**
   * Vazão real, em mensagens por minuto.
   *
   * É a MÉDIA desde o início, não uma janela deslizante: uma janela exigiria
   * guardar histórico no cliente e escolher um tamanho, e o primeiro quadro
   * depois de um F5 não teria nenhum. A média responde a pergunta que o
   * operador realmente faz — "nesse ritmo, quando acaba?" — e é a mesma base do
   * tempo restante logo abaixo, então os dois números nunca se contradizem.
   */
  get throughputPerMinute(): number | null {
    const elapsed = this.elapsedMs;
    if (elapsed === null || elapsed < CampaignMessageComponent.MIN_ELAPSED_FOR_RATE_MS) {
      return null;
    }
    const processed = this.concludedCount;
    if (processed <= 0) {
      return null;
    }
    return processed / (elapsed / MINUTE_MS);
  }

  /** Minutos restantes ao ritmo atual. `null` quando não há base para estimar. */
  get remainingMinutes(): number | null {
    if (!this.isRunning || this.totalPendingCount <= 0) {
      return null;
    }
    const rate = this.throughputPerMinute;
    if (!rate || rate <= 0) {
      return null;
    }
    return this.totalPendingCount / rate;
  }

  /**
   * O tempo restante já escrito.
   *
   * Formatado aqui e não no template porque a unidade muda com a grandeza —
   * "menos de 1 min", "24 min", "1 h 20 min" — e três `@if` aninhados no HTML
   * para escolher a frase é pior de ler do que a escolha explícita.
   *
   * `this.t()` num getter é seguro: ele roda no render, quando o catálogo já
   * carregou — ao contrário de uma chamada em `ngOnInit`, que devolve a chave.
   */
  get remainingLabel(): string | null {
    const minutes = this.remainingMinutes;
    if (minutes === null) {
      return null;
    }
    if (minutes < 1) {
      return this.t('campaign.message.progressUnderMinute');
    }
    if (minutes < 60) {
      return this.t('campaign.message.progressMinutes', { minutes: Math.round(minutes) });
    }
    return this.t('campaign.message.progressHoursMinutes', {
      hours: Math.floor(minutes / 60),
      minutes: Math.round(minutes % 60),
    });
  }

  /** Aceitas sobre o lote endereçável — usado onde o canal não confirma entrega. */
  get sentPercent(): number {
    return this.batchTotal > 0 ? (this.totalSentCount / this.batchTotal) * 100 : 0;
  }

  /**
   * As faixas da barra, na ordem em que aparecem.
   *
   * As faixas vêm PRONTAS da agregação e nada aqui subtrai um contador do
   * outro: `sent - delivered` contaria de novo a que falhou depois do aceite,
   * e no RCS a leitura chega sem entrega, o que jogaria a mesma mensagem em
   * duas faixas.
   *
   * `deliveredUnread` e `unconfirmed` excluem falha por construção. `read` não
   * — ele é o contador do cartão "Lidas", que por definição é `read: true` —
   * então uma mensagem lida e depois recusada conta em duas faixas. É raro o
   * bastante para não valer um contador só para a barra: o efeito é a barra
   * reescalar, não mentir sobre o total.
   *
   * A fila é a exceção, e é derivada logo abaixo — não vem de `pending`, que
   * hoje é contado por `status` e responde a outra pergunta.
   *
   * Uma composição só, para todo canal e todo instante. Faixas por estado de
   * recibo dispensam qualquer variante: sem recibo nenhum, `unconfirmed` já é
   * o lote aceito inteiro, e as que ainda não foram aceitas caem na fila.
   * `sent` não serve de faixa porque não é exclusivo com `failed` — a que
   * falha depois do aceite continua contando nos dois, e a barra passaria de
   * 100% com a fila grudada em zero.
   */
  get progressSegments(): Array<ProgressSegment> {
    if (!this.showProgress) {
      return [];
    }

    const base = this.batchTotal;
    const segments: Array<ProgressSegment> = [
      { key: 'read', labelKey: 'campaign.message.segmentRead', count: this.readCount, percent: 0, tone: 'read' },
      { key: 'delivered', labelKey: 'campaign.message.segmentDeliveredUnread', count: this.deliveredUnreadCount, percent: 0, tone: 'ok' },
      { key: 'unconfirmed', labelKey: 'campaign.message.segmentUnconfirmed', count: this.unconfirmedCount, percent: 0, tone: 'warn' },
      { key: 'failed', labelKey: 'campaign.message.segmentFailed', count: this.totalFailedCount, percent: 0, tone: 'err' },
    ];

    // A fila é o resto, e não `pending` direto: as faixas acima já são
    // exclusivas, então o resto É a fila — e escrevê-lo como subtração garante
    // que a barra feche em 100% mesmo que uma escrita concorrente tenha
    // mudado um contador entre o `$group` e a leitura.
    const accounted = segments.reduce((acc, segment) => acc + segment.count, 0);
    segments.push({
      key: 'queued',
      labelKey: 'campaign.message.segmentQueued',
      count: Math.max(0, base - accounted),
      percent: 0,
      tone: 'queue',
    });

    return segments.map((segment) => ({
      ...segment,
      percent: base > 0 ? (segment.count / base) * 100 : 0,
    }));
  }

  /** Só as faixas com alguma mensagem — a legenda não lista o que não existe. */
  get visibleSegments(): Array<ProgressSegment> {
    return this.progressSegments.filter((segment) => segment.count > 0);
  }

  /**
   * Há custo para mostrar.
   *
   * `false` enquanto o disparo executa e sempre em canal não tarifado — nos
   * dois casos `Campaign.cost` não existe, e a tela mostra travessão.
   */
  get costSettled(): boolean {
    return this.realCost !== null;
  }

  private rateOfBatch(count: number): number {
    return this.batchTotal > 0 ? (count / this.batchTotal) * 100 : 0;
  }

  private applyLatency(latency: CampaignMessageLatency): void {
    this.latency = latency ?? null;
    this.chartSeriesNames = [
      this.t('campaign.message.chartSent'),
      this.t('campaign.message.chartDelivered'),
      this.t('campaign.message.chartRead'),
    ];
    this.latencyChart = this.buildLatencyChart(this.latency);
  }

  /**
   * One line per state, one point per band, y in cumulative message counts.
   *
   * The x-axis is the FIXED list of bands the backend returns — the same
   * seven points whether the dispatch took three minutes or three days. The
   * per-minute curve this replaces grew one point per minute that had an
   * event, so a single read receipt a day later stretched it into a wall of
   * rotated labels while the minutes that mattered collapsed into a sliver.
   *
   * Sent is flat by construction — every message is "sent" at its own zero —
   * and is kept as the ceiling: in counts, the gap between it and the
   * delivered line at the last band is exactly what never arrived, which a
   * chart of receipts alone would leave the reader to work out from the KPIs.
   *
   * Empty until a receipt exists: on a channel that confirms nothing, a flat
   * sent line over two zero lines would read as a broken chart, not as "no
   * receipts" — the subtitle carries that message instead.
   */
  private buildLatencyChart(latency: CampaignMessageLatency | null): LatencySeries[] {
    if (!latency || latency.sent <= 0 || !this.hasReceipts) {
      return [];
    }

    const [sentName, deliveredName, readName] = this.chartSeriesNames;
    const bands = latency.bands ?? [];
    const labels = bands.map((band, index) => this.bandLabel(band, index > 0 ? bands[index - 1] : null));

    return [
      { name: sentName, series: labels.map((label) => ({ name: label, value: latency.sent })) },
      { name: deliveredName, series: labels.map((label, i) => ({ name: label, value: bands[i].delivered ?? 0 })) },
      { name: readName, series: labels.map((label, i) => ({ name: label, value: bands[i].read ?? 0 })) },
    ];
  }

  /**
   * Axis label of a band: its upper bound as a duration, or "+<previous>" for
   * the unbounded last band — "1min", "6h", "1d", "+1d".
   */
  private bandLabel(band: CampaignMessageLatencyBand, previous: CampaignMessageLatencyBand | null): string {
    if (band.upToMs === null) {
      return this.t('campaign.message.chartBeyondLabel', {
        label: previous?.upToMs != null ? this.durationLabel(previous.upToMs) : '',
      });
    }
    return this.durationLabel(band.upToMs);
  }

  /**
   * A duration in the largest unit it fills exactly: "15min", "6h", "1d".
   *
   * Once it reaches 24h the unit is the day — "1d", never "24h": the bands are
   * fixed round values, so there is no remainder to carry ("1h 05min" belonged
   * to the per-minute curve, where any offset could occur).
   */
  private durationLabel(ms: number): string {
    const minutes = Math.round(ms / MINUTE_MS);
    const minutesPerDay = 24 * 60;
    if (minutes >= minutesPerDay) {
      return this.t('campaign.message.chartDayLabel', { days: Math.round(minutes / minutesPerDay) });
    }
    if (minutes >= 60) {
      return this.t('campaign.message.chartHourLabel', { hours: Math.round(minutes / 60) });
    }
    return this.t('campaign.message.chartMinuteLabel', { minute: minutes });
  }

  /** Half of the sent messages delivered within… — the chart's headline figure. */
  get halfDeliveredLabel(): string {
    return this.halfBandLabel('delivered');
  }

  /** Half of the sent messages read within… */
  get halfReadLabel(): string {
    return this.halfBandLabel('read');
  }

  /**
   * The first band in which at least half of the accepted messages carry the
   * receipt — a median read straight off the bands, with no per-message data.
   *
   * Exact enough for the question asked ("did people get it in minutes or in
   * hours?") and it costs nothing: `$percentile` needs MongoDB 7 and a
   * per-message array would move thousands of numbers to the browser to print
   * one word. When even the last band stays under half, that IS the finding —
   * said in words, not hidden behind a dash.
   */
  private halfBandLabel(kind: 'delivered' | 'read'): string {
    const latency = this.latency;
    if (!latency || latency.sent <= 0) {
      return '—';
    }
    const bands = latency.bands ?? [];
    const index = bands.findIndex((band) => (band[kind] ?? 0) / latency.sent >= 0.5);
    if (index < 0) {
      return this.t('campaign.message.chartBelowHalf');
    }
    const band = bands[index];
    if (band.upToMs === null) {
      return this.bandLabel(band, index > 0 ? bands[index - 1] : null);
    }
    return this.t('campaign.message.chartWithinLabel', { label: this.durationLabel(band.upToMs) });
  }

  /** At least one receipt arrived — same rule as `receiptsAvailable`, read off the latency. */
  get hasReceipts(): boolean {
    return (this.latency?.delivered ?? 0) > 0 || (this.latency?.read ?? 0) > 0;
  }

  /** Counts have no halves: only whole-number ticks get a label. */
  readonly formatCount = (value: number): string =>
    Number.isInteger(value) ? String(value) : '';

  /**
   * O custo do disparo: `Campaign.cost`, liquidado no razão.
   *
   * `null` quando não existe — disparo em execução, ou canal não tarifado.
   * Ausência não é zero, e a tela mostra travessão em vez de R$ 0,00.
   */
  get realCost(): number | null {
    return this.progress?.cost?.estimated ?? this.campaign?.cost ?? null;
  }


  /* ────────────────────────────────────────────────────────────────────
     Pré-visualização da mensagem — estática
     ──────────────────────────────────────────────────────────────────── */

  previewOpen = true;

  togglePreview(): void {
    this.previewOpen = !this.previewOpen;
  }

  get zapiPreviewData(): ZAPIData | null {
    return this.channelType === ChannelTypeEnum.Z_API
      ? this.campaign?.metadata?.zapiData ?? null
      : null;
  }

  get rcsPreviewData(): RCSData | null {
    return this.channelType === ChannelTypeEnum.RCS_API
      ? this.campaign?.metadata?.rcsData ?? null
      : null;
  }

  get cloudApiPreviewData(): CloudAPIData | null {
    if (this.channelType === ChannelTypeEnum.Z_API || this.channelType === ChannelTypeEnum.RCS_API) {
      return null;
    }
    return this.campaign?.metadata?.cloudAPIData ?? null;
  }

  get isZapiButtonsPreview(): boolean {
    return this.zapiPreviewData?.messageType === 'buttons';
  }

  get hasPreview(): boolean {
    return !!this.zapiPreviewData || !!this.rcsPreviewData || !!this.cloudApiPreviewData?.template;
  }

  translateStatus(status: CampaignStatusEnum): string {
    this.campaignStatus = {
      [CampaignStatusEnum.WAITING]: 'campaign.message.statusWaiting',
      [CampaignStatusEnum.SENDING]: 'campaign.message.statusSending',
      [CampaignStatusEnum.DONE]: 'campaign.message.statusDone',
      [CampaignStatusEnum.CANCELED]: 'campaign.message.statusCanceled',
      [CampaignStatusEnum.ERROR]: 'campaign.message.statusError',
    };
    return this.t(this.campaignStatus[status] ?? 'campaign.message.statusUnknown');
  }

  get canRestartOrContinue(): boolean {
    return !!this.campaign
      && this.campaign.status !== CampaignStatusEnum.CANCELED
      && this.campaign.enabled !== false;
  }

  restartOrContinue() {
    if (!this.canRestartOrContinue) {
      return;
    }

    this.campaignService.restartOrContinueCampaign(this.campaign._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.alertService.success();
          // Progress and rows together: "Continuar" re-queues recipients, and
          // a table still showing them as failed would contradict the cards.
          this.refreshQuietly(this.campaign._id);
        },
        error: (err) => this.alertService.error(
          err?.error?.message ?? this.t('campaign.message.restartError'),
        ),
      });
  }

  updatePage() {
    this.loading = true;
    this.init(this.campaign._id);
  }

  getTimeSpent(): string {
    if (this.campaign.startedAt && this.campaign.endedAt) {
      const diffMs = new Date(this.campaign.endedAt).getTime() - new Date(this.campaign.startedAt).getTime();
      const hours = Math.floor(diffMs / 3600000); // horas
      const minutes = Math.floor((diffMs % 3600000) / 60000); // minutos
      const seconds = Math.floor((diffMs % 60000) / 1000); // segundos
      const dddddd = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      return dddddd;
    }
    return '';
  }

}
